import React, { useState, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { HandleFileUpload, HandleExport } from './utils';
import { dateOptions } from './options';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import BaseTransactions from './Tables';
import GlTransactions from './GlTable';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import styles from './FileSelect.module.css'
import AddDynamicInputFields from "./openingBalance"

const FileSelect = (basisObj) => {
    // make a copy of the basis object
    const [csvfilename, setCsvFileName] = useState('');
    const [fileObject, setFileObject] = useState(null);
    const [dateFormat, setDateFormat] = useState(dateOptions[0].value);
    const [basisObjX, setBasisObjX] = useState(basisObj);
    const [csvBase, setCsvBase] = useState('');
    const [isVisibleBase, setIsVisibleBase] = useState(false);
    const [isVisibleGL, setIsVisibleGL] = useState(false);
    const [isVisibleButtons, setIsVisibleButtons] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const [result, setResult] = useState([]);

    // CSV preview confirm dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [confirmValid, setConfirmValid] = useState(true);
    const [confirmErrors, setConfirmErrors] = useState([]);
    const confirmResolveRef = useRef(null);

    const showConfirm = ({ text, isValid, errors }) => {
        return new Promise((resolve) => {
            confirmResolveRef.current = resolve;
            setConfirmText(text);
            setConfirmValid(isValid);
            setConfirmErrors(errors || []);
            setConfirmOpen(true);
        });
    };

    const handleConfirmOk = () => {
        setConfirmOpen(false);
        if (confirmResolveRef.current) confirmResolveRef.current(true);
    };

    const handleConfirmCancel = () => {
        setConfirmOpen(false);
        if (confirmResolveRef.current) confirmResolveRef.current(false);
    };

    const handleFile = (event) => {
        //console.log('handleFile', event.target.files[0]);
        setCsvFileName(event.target.files[0].name)
        setFileObject(event.target.files[0]);
    }

    const handleSelect = (event) => {
        document.getElementById("select-file").click()
    }

    const handleDateFormat = (event) => {
        //basisObj.dateFormat = event.target.value;
        setDateFormat(event.target.value);
    }

    function handleRates(result, ob = []) {
        // Sort OB entries ascending by date index (oldest first), then unshift
        // in reverse so that the oldest entry lands at result[0] (FIFO order).
        const sortedOb = [...ob].sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
        for (let i = sortedOb.length - 1; i >= 0; i--) {
            result.unshift(sortedOb[i]);
        }
        if (document.getElementById('result-copy').value === '') {
            document.getElementById('result-copy').value = JSON.stringify(result);
        }

        setCsvBase(result);
        setIsVisibleButtons(true);
        // If the GL view is already open (e.g. called from Set Balance), stay there.
        // Otherwise default to showing Base Transactions after a fresh file load.
        if (!isVisibleGL) {
            setIsVisibleBase(true);
            setIsVisibleGL(false);
        }
        setResult(result);
    }

    const handleUpload = (event) => {
        if (fileObject === null) {
            alert("Select a file to process...");
            return false;
        }
        document.getElementById("result-copy").value = "";
        HandleFileUpload(fileObject, basisObj, handleRates, showConfirm);
    };

    const viewBase = (event) => {
        setIsPressed(true);
        setIsVisibleBase(true);
        setIsVisibleGL(false);
    }

    const viewGL = (event) => {
        setIsVisibleBase(false);
        setIsVisibleGL(true);
    }

    const printAny = (event) => {
        if (isVisibleBase) {
            HandleExport("base-transactions", fileObject, basisObj)
        } else {
            HandleExport("gl-transactions", fileObject, basisObj)
        }
    }

    useEffect(() => {
        let basisObjX = { ...basisObj };
        try {
            basisObjX.date_format = dateFormat;
            setBasisObjX(basisObjX);
        } catch (e) {
            //console.log("Undefined...passing");
        }
    }, [dateFormat, isPressed, result, basisObj]);

    return (
        <React.Fragment>
            <Grid item xs={2}>
                <FormControl fullWidth >
                    <Button sx={{
                        height: '39px',
                    }}
                        component="label"
                        //size="small"
                        role={undefined}
                        variant="contained"
                        onClick={handleSelect}
                    >
                        Select file
                    </Button>

                    <input type="file" style={{
                        clip: 'rect(0 0 0 0)',
                        clipPath: 'inset(50%)',
                        height: 10,
                        overflow: 'hidden',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        whiteSpace: 'nowrap',
                        width: 10,
                    }}
                        onChange={handleFile}
                        id="select-file"
                    />
                </FormControl>
            </Grid>
            <Grid item xs={6}>
                <FormControl fullWidth>
                    <TextField
                        readOnly
                        id="csvfile-id"
                        label="Selected file"
                        name="csvfile"
                        disabled
                        value={csvfilename}
                        size="small"
                    //inputProps={{ style: { textAlign: 'right' } }} // the change is here
                    />
                </FormControl>
            </Grid>
            <Grid item xs={2}>
                <FormControl fullWidth >
                    <InputLabel sx={{ pt: 0.1, pb: 0.1 }} id="date-format">Date format</InputLabel>
                    <Select
                        labelId="date-format"
                        label="Date format"
                        name="date-format"
                        id="dateformat"
                        size="small"
                        value={dateFormat}
                        onChange={handleDateFormat}
                    >
                        {dateOptions.map((dateFormat) => (
                            <MenuItem key={dateFormat.value} value={dateFormat.value}>
                                {dateFormat.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={2}>
                <FormControl fullWidth >
                    <Button sx={{ height: "39px" }}
                        variant="contained"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleUpload}
                    >
                        Upload
                    </Button>
                </FormControl>
            </Grid>
            <Grid item xs={4} className={isVisibleButtons ? styles.visible : styles.hidden}>
                <FormControl fullWidth >
                    <Button sx={{
                        height: '39px',
                    }}
                        component="label"
                        //size="small"
                        role={undefined}
                        variant="contained"
                        onClick={viewBase}
                    >
                        Base Transactions
                    </Button>
                </FormControl>
            </Grid>
            <Grid item xs={4} className={isVisibleButtons ? styles.visible : styles.hidden}>
                <FormControl fullWidth >
                    <Button sx={{
                        height: '39px',
                    }}
                        component="label"
                        //size="small"
                        role={undefined}
                        variant="contained"
                        onClick={viewGL}
                    >
                        Transaction Gain/Loss
                    </Button>
                </FormControl>
            </Grid>
            <Grid item xs={4} className={isVisibleButtons ? styles.visible : styles.hidden}>
                <FormControl fullWidth >
                    <Button sx={{
                        height: '39px',
                    }}
                        component="label"
                        //size="small"
                        role={undefined}
                        variant="contained"
                        onClick={printAny}
                    >
                        Download transactions
                    </Button>
                </FormControl>
            </Grid>

            <Grid item xs={12} id="base-transactions" className={isVisibleBase ? styles.visible : styles.hidden}>
                <b style={{fontSize:'20px'}}>Base Transactions</b>
                <BaseTransactions data={csvBase} basis={basisObj.basisObj} />
            </Grid>
            <Grid item xs={12} id="gl-main" className={isVisibleGL ? styles.visible : styles.hidden}>
                <b style={{fontSize:'20px'}}>Transaction Gain/Loss</b>
                <AddDynamicInputFields result={result} callback={handleRates} />
                <GlTransactions data={csvBase} basis={basisObj.basisObj} />
            </Grid>

            {/* CSV Preview Confirm Dialog */}
            <Dialog
                open={confirmOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleConfirmCancel();
                    }
                }}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Confirm file contents (first 10 rows)</DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Required file format
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Your CSV file must contain the following columns in order:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            <li><Typography variant="body2"><strong>Date</strong> — transaction date (see accepted formats below)</Typography></li>
                            <li><Typography variant="body2"><strong>Description</strong> — free-text description of the transaction</Typography></li>
                            <li><Typography variant="body2"><strong>Amount</strong> — transaction amount in the foreign currency (negative for debits)</Typography></li>
                            <li><Typography variant="body2" color="text.secondary"><em>Rate (optional)</em> — if provided, this exchange rate will be used directly instead of the lookup tables</Typography></li>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                            <strong>Accepted date formats</strong> (select matching format in the &ldquo;Date format&rdquo; dropdown):
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            <li><Typography variant="body2"><code>dd/mm/yyyy</code> or <code>dd-mm-yyyy</code> &mdash; e.g. 22/06/2023</Typography></li>
                            <li><Typography variant="body2"><code>mm/dd/yyyy</code> or <code>mm-dd-yyyy</code> &mdash; e.g. 06/22/2023</Typography></li>
                            <li><Typography variant="body2"><code>yyyy/mm/dd</code> or <code>yyyy-mm-dd</code> &mdash; e.g. 2023-06-22</Typography></li>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                            Columns may be separated by a <strong>comma</strong> <code>,</code>, <strong>tab</strong>, <strong>pipe</strong> <code>|</code>, or <strong>semicolon</strong> <code>;</code> — the delimiter is detected automatically. The first row may optionally be a header row.
                        </Typography>
                    </Alert>
                    {confirmErrors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                File format errors — please fix before uploading:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {confirmErrors.map((err, i) => (
                                    <li key={i}><Typography variant="body2">{err}</Typography></li>
                                ))}
                            </Box>
                        </Alert>
                    )}
                    <Box
                        component="pre"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            whiteSpace: 'pre',
                            overflowX: 'auto',
                            m: 0,
                            p: 1,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                        }}
                    >
                        {confirmText}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmCancel} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleConfirmOk}
                        variant="contained"
                        disabled={!confirmValid}
                        title={!confirmValid ? 'Fix the errors above before proceeding' : ''}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default FileSelect;
